import React, { useMemo, useState } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Calendar } from 'shadcn/components/ui/Calendar';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';

function DateRangeFilter({
    id,
    includeTime = false,
    label,
    onChange,
    placeholder = 'Pick a date',
    range = true,
    value,
}) {
    const [open, setOpen] = useState(false);
    const atLocalNoon = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0) : undefined);
    const pad = (n) => String(n).padStart(2, '0');
    const toInputValue = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        if (!includeTime) return `${year}-${month}-${day}`;
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const display = useMemo(() => {
        if (range) {
            if (value && value.startDate && value.endDate) {
                if (includeTime) {
                    const fmt = (d) =>
                        new Date(d).toLocaleString([], {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    return `${fmt(value.startDate)} - ${fmt(value.endDate)}`;
                }
                return `${new Date(value.startDate).toLocaleDateString()} - ${new Date(value.endDate).toLocaleDateString()}`;
            }
            return placeholder || 'Pick a date range';
        }
        const single = value?.date ?? value;
        if (single) {
            if (includeTime) {
                return new Date(single).toLocaleString([], {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
            return new Date(single).toLocaleDateString();
        }
        return placeholder || 'Pick a date';
    }, [value, placeholder, range, includeTime]);

    const selected = useMemo(() => {
        if (range) {
            if (value && value.startDate && value.endDate) {
                return { from: new Date(value.startDate), to: new Date(value.endDate) };
            }
            return { from: undefined, to: undefined };
        }
        const single = value?.date ?? value;
        return single ? new Date(single) : undefined;
    }, [value, range]);

    const startDateObj = value?.startDate ? new Date(value.startDate) : undefined;
    const endDateObj = value?.endDate ? new Date(value.endDate) : undefined;
    const singleDateObj = value?.date
        ? new Date(value.date)
        : value && !value.startDate && !value.endDate
          ? new Date(value)
          : undefined;

    return (
        <div className="flex flex-col gap-1 min-w-[220px]">
            {label && <Label htmlFor={id}>{label}</Label>}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                        {display}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    {includeTime ? (
                        <div className="px-3 py-3">
                            {range ? (
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor={`${id}-start`}>Start</Label>
                                        <Input
                                            id={`${id}-start`}
                                            type="datetime-local"
                                            value={toInputValue(startDateObj)}
                                            onChange={(e) => {
                                                const picked = e.target.value ? new Date(e.target.value) : undefined;
                                                onChange({ startDate: picked, endDate: endDateObj });
                                            }}
                                            className="h-7 px-2 text-sm w-full"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor={`${id}-end`}>End</Label>
                                        <Input
                                            id={`${id}-end`}
                                            type="datetime-local"
                                            value={toInputValue(endDateObj)}
                                            onChange={(e) => {
                                                const picked = e.target.value ? new Date(e.target.value) : undefined;
                                                onChange({ startDate: startDateObj, endDate: picked });
                                            }}
                                            className="h-7 px-2 text-sm w-full"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <Label htmlFor={id}>{label || 'Date'}</Label>
                                    <Input
                                        id={id}
                                        type="datetime-local"
                                        value={toInputValue(singleDateObj)}
                                        onChange={(e) => {
                                            const picked = e.target.value ? new Date(e.target.value) : undefined;
                                            onChange({ date: picked });
                                        }}
                                        className="h-7 px-2 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-3">
                            <Calendar
                                mode={range ? 'range' : 'single'}
                                selected={selected}
                                onSelect={(picked) => {
                                    if (range) {
                                        if (picked && picked.from && picked.to) {
                                            onChange({
                                                startDate: atLocalNoon(picked.from),
                                                endDate: atLocalNoon(picked.to),
                                            });
                                        }
                                    } else if (picked) {
                                        onChange({ date: atLocalNoon(picked) });
                                    }
                                }}
                                numberOfMonths={range ? 2 : 1}
                            />
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}

export { DateRangeFilter };

// Menu variant: renders input fields that open date pickers; selecting a date will not close the dropdown
function DateRangeFilterMenuItem({
    id,
    includeTime = false,
    label,
    onChange,
    placeholder = 'Pick a date',
    range = true,
    value,
}) {
    const startLabel = 'Start Date';
    const endLabel = 'End Date';

    const startDateObj = value?.startDate ? new Date(value.startDate) : undefined;
    const endDateObj = value?.endDate ? new Date(value.endDate) : undefined;
    const singleDateObj = value?.date
        ? new Date(value.date)
        : value && !value.startDate && !value.endDate
          ? new Date(value)
          : undefined;

    const pad = (n) => String(n).padStart(2, '0');
    const toInputValue = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        if (!includeTime) return `${year}-${month}-${day}`;
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const inputType = includeTime ? 'datetime-local' : 'date';
    const parseLocalDateFromInput = (str) => {
        if (!str) return undefined;
        const parts = str.split('-');
        if (parts.length < 3) return undefined;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const d = Number(parts[2]);
        if (!y || !m || !d) return undefined;
        return new Date(y, m - 1, d, 12, 0, 0, 0);
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-0">
                <div className="px-2 py-2">
                    {range ? (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-start`}>{startLabel}</Label>
                                <Input
                                    id={`${id}-start`}
                                    type={inputType}
                                    value={toInputValue(startDateObj)}
                                    onChange={(e) => {
                                        const picked = e.target.value
                                            ? inputType === 'date'
                                                ? parseLocalDateFromInput(e.target.value)
                                                : new Date(e.target.value)
                                            : undefined;
                                        onChange({ startDate: picked, endDate: endDateObj });
                                    }}
                                    className="h-7 px-2 text-sm w-full"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-end`}>{endLabel}</Label>
                                <Input
                                    id={`${id}-end`}
                                    type={inputType}
                                    value={toInputValue(endDateObj)}
                                    onChange={(e) => {
                                        const picked = e.target.value
                                            ? inputType === 'date'
                                                ? parseLocalDateFromInput(e.target.value)
                                                : new Date(e.target.value)
                                            : undefined;
                                        onChange({ startDate: startDateObj, endDate: picked });
                                    }}
                                    className="h-7 px-2 text-sm w-full"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            <Label htmlFor={id}>{label}</Label>
                            <Input
                                id={id}
                                type={inputType}
                                value={toInputValue(singleDateObj)}
                                onChange={(e) => {
                                    const picked = e.target.value
                                        ? inputType === 'date'
                                            ? parseLocalDateFromInput(e.target.value)
                                            : new Date(e.target.value)
                                        : undefined;
                                    onChange({ date: picked });
                                }}
                                className="h-7 px-2 text-sm"
                            />
                        </div>
                    )}
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export { DateRangeFilterMenuItem };
